import {storeToRefs} from 'pinia'
import {useAuthStore, useGivingTreeStore, useMainStore, useSlidesStore} from '@/store'
import createTree from "@lpiadmin/giving-tree";
import {SlidesState} from "@/store/slides";
import {Ref} from "vue";
import {newSlide} from "@/mocks/newSlide";
import useAddSlidesOrElements from "@/hooks/useAddSlidesOrElements";

let lastSave = ''

export default () => {
    const givingTreeStore = useGivingTreeStore()
    const mainStore = useMainStore()
    const authStore = useAuthStore()
    const slidesStore = useSlidesStore()
    const { addSlidesFromData } = useAddSlidesOrElements()


    const {valueModelLoaded, slideEditorLoading, vpSlideId, editor, calculatorId, slideLibraryChooser, saving, lastModifiedInfo } = storeToRefs(mainStore)
    const {loggedInUser} = storeToRefs(authStore)
    const {valueModelId, author, created, updated, deckTitle, rules, rulesEnabled} = storeToRefs(slidesStore)



    const isSame = (state) => {
        return JSON.stringify(state) === lastSave
    }

    const forceSavePresentation = async () => {

        const currentDateTime = new Date().toISOString();
        const params = new URLSearchParams(window.location.search);

        const {path, slides, theme, slideIndex, rules, viewportRatio,
            valueModelId} = storeToRefs(slidesStore)

        const state = {
            path: path.value,
            theme: theme.value,
            slides: slides.value,
            viewportRatio: viewportRatio.value,
            valueModelId: valueModelId.value,
            deckTitle: deckTitle.value,
            rules: rules.value,
            rulesEnabled: rulesEnabled.value
        }



        saving.value = true

        lastSave = JSON.stringify(state)


        if (!slidesStore.created) {
            slidesStore.created = currentDateTime;
        }

        slidesStore.updated = currentDateTime
        state.author = loggedInUser.value
        state.created = created.value
        state.updated = updated.value
        state.slideIndex = slideIndex.value

        const apiPath = path.value || params.get('id') || params.get('slideLibrary');

        const calcId = params.get('calcId')
        if (calcId) await saveSlideToCalcBucket(calcId, state)



        const result = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/slide-editor/${apiPath}`, {
            method: 'PUT',
            headers: {
                'content-type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify(state),
        });

        saving.value = false
        lastModifiedInfo.value = {
            author: loggedInUser.value,
            lastModifiedDate: Date.now().valueOf()
        }
        return result
    }

    const savePresentation = async () => {

        const currentDateTime = new Date().toISOString();
        const params = new URLSearchParams(window.location.search);

        const {path, slides, theme, slideIndex, rules, viewportRatio,
            valueModelId} = storeToRefs(slidesStore)

        const state = {
            path: path.value,
            theme: theme.value,
            slides: slides.value,
            viewportRatio: viewportRatio.value,
            valueModelId: valueModelId.value,
            deckTitle: deckTitle.value,
            rules: rules.value,
            rulesEnabled: rulesEnabled.value
        }

        if (isSame(state)) {
            return
        }

        saving.value = true

        lastSave = JSON.stringify(state)


        if (!slidesStore.created) {
            slidesStore.created = currentDateTime;
        }

        slidesStore.updated = currentDateTime
        state.author = loggedInUser.value
        state.created = created.value
        state.updated = updated.value
        state.slideIndex = slideIndex.value

        const apiPath = path.value || params.get('id') || params.get('slideLibrary');

        const calcId = params.get('calcId')
        if (calcId) await saveSlideToCalcBucket(calcId, state)



        const result = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/slide-editor/${apiPath}`, {
            method: 'PUT',
            headers: {
                'content-type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify(state),
        });

        saving.value = false
        lastModifiedInfo.value = {
            author: loggedInUser.value,
            lastModifiedDate: Date.now().valueOf()
        }
        return result
    }


    const publishPresentation = async () => {

        const currentDateTime = new Date().toISOString();
        const params = new URLSearchParams(window.location.search);


        if (!slidesStore.created) {
            slidesStore.created = currentDateTime;
        }

        slidesStore.updated = currentDateTime

        const {path, slides, theme, slideIndex, viewportRatio, valueModelId} = storeToRefs(slidesStore)

        const state = {
            path: path.value,
            theme: theme.value,
            slides: slides.value,
            slideIndex: slideIndex.value,
            viewportRatio: viewportRatio.value,
            valueModelId: valueModelId.value,
            deckTitle: deckTitle.value,
            author: loggedInUser.value,
            created: created.value,
            updated: updated.value,
            rules: rules.value,
            rulesEnabled: rulesEnabled.value
        }


        const apiPath = path.value || params.get('id');

        return await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/slide-editor/published/${apiPath}`, {
            method: 'PUT',
            headers: {
                'content-type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify(state),
        });
    }

    const getModified = async () => {
        const response = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/last-modified/${valueModelId.value}/${vpSlideId.value}`, {
            method: 'GET'
        })

        return await response.json()
    }

    const getVpSlide = async (vmId: string, vpSlideId: Ref<string>) => {
        const response = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/value-prop-slide/${vmId}/${vpSlideId.value}`, {
            method: 'GET'
        })

        return await response.json()
    }




    const setVpSlideData = async (data: SlidesState, vmId: string) => {
        slidesStore.path = `${vmId}/${vpSlideId.value}`;
        slidesStore.setSlides(data.slides);
        slidesStore.setViewportRatio(data.viewportRatio);
        slidesStore.setTheme(data.theme);
        slidesStore.updateSlideIndex(data.slideIndex);
        slidesStore.setValueModelId(data.valueModelId || Number(vmId));
        author.value = data.author ?? null;
        created.value = data.created ?? null;
        updated.value = data.updated ?? null;
        deckTitle.value = data.deckTitle ?? null;
        rules.value = data.rules ?? null
        rulesEnabled.value = data.rulesEnabled ?? true


        const {path, slides, theme, viewportRatio,
            valueModelId} = storeToRefs(slidesStore)
        const state = {
            path: path.value,
            theme: theme.value,
            slides: slides.value,
            viewportRatio: viewportRatio.value,
            valueModelId: valueModelId.value,
            deckTitle: deckTitle.value,
            rules: rules.value,
            rulesEnabled: rulesEnabled.value
        }
        lastSave = JSON.stringify(state)
    }

    const setUpSlide = async (paramId: string, modelId: string, slideLibraryId: string, isPreview = true) => {


        const vmId = window['valuePropJson']?.ValueModelId || valueModelId.value || modelId || 0

        const id = isPreview ? vmId : window['valuePropJson']?.VPId

        if (!vpSlideId.value) vpSlideId.value = <string>paramId || <string>slideLibraryId

        const data = await getVpSlide(id, vpSlideId)

        if (data.isNew) {
            const newSlideCopy = JSON.parse(JSON.stringify(newSlide))

            data.data.slides = addSlidesFromData(newSlideCopy);

        }

        await setVpSlideData(data.data, vmId)
    }



    const updateSlide = async () => {
        const params = new URLSearchParams(window.location.search)
        const vmId = window['valuePropJson']?.ValueModelId || valueModelId.value || params.get('vmId')

        if (!vpSlideId.value) vpSlideId.value = <string>params.get('deckId')

        const data = await getVpSlide(vmId, vpSlideId)

        slidesStore.updateSlide(data.slides[0])
    }

    const loadValueModel = async (paramId: string, vmId: string, calcId = '', isPreview: boolean) => {
        if (window['valuePropJson']) {
            window['tree'] = createTree(window['valuePropJson'], givingTreeStore)
            isPreview ? await setUpSlide(paramId, vmId) : await setUpSlide(paramId, vmId, '', isPreview)
            valueModelLoaded.value = true
        } else if (calcId) {
            calculatorId.value = calcId
            const vmId = await getCalculatorModel(calcId)
            await setUpCalculator(calcId, vmId)

            valueModelLoaded.value = true
            setTimeout(() => slideEditorLoading.value = false, 1000)

        } else if (paramId && vmId) {

            await setUpSlide(paramId, vmId, '')
            await getLatestValueModel(null)

            valueModelLoaded.value = true
            setTimeout(() => slideEditorLoading.value = false, 1000)


        } else {
            console.error('Cannot load anything.')
        }
    }

    const setUpCalculator = async (calcId: string, vmId: string) => {
        if (!vpSlideId.value) vpSlideId.value = <string>calcId
        //TODO: update if calculators will have more than 1 slide in the future
        slidesStore.slides[0].infiniteScroll = true
        const data = editor.value ? await getVpSlide(vmId, vpSlideId) : await getSlideFromCalcBucket(calcId)
        const slideData = data.data ? data.data : data
        await setVpSlideData(slideData, vmId)
    }

    const getCalculatorModel = async (calcId: string) => {
        const response = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/public-vp/data/${calcId}/model.json`, {
            method: 'GET'
        })

        const json = await response.json()
        window['tree'] = createTree(json, givingTreeStore)

        return json.ValueModelId
    }

    const lastUpdated = {}

    const initValueModelEventListener = () => {
        console.log('SE adding event listener')
        document.addEventListener("modelUpdatedEvent", function (event: any) {
            console.log('SE got event', event.detail)
            const {hookId, field, updated, source} = event.detail
            // ignore any pinia updates because that would be circular
            if ('pinia' !== source) {

                // also throttle these updates to break loops / spam
                if (field && updateAllowed(field)) {
                    lastUpdated[field] = Date.now()
                    givingTreeStore.syncMetaFromApi(field, updated)
                }

                if (hookId && updateAllowed(hookId)) {
                    lastUpdated[hookId] = Date.now()
                    givingTreeStore.syncVariableFromApi(hookId, updated)
                }
            }
        });
    }

    const updateAllowed = (key) => {
        console.log('SE checking update', key in lastUpdated ? Date.now() - lastUpdated[key] : null)
        return !(key in lastUpdated && Date.now() - lastUpdated[key] < 250)
    }

    const getLatestValueModel = async (vmId) => {

        const id = vmId ? vmId : valueModelId.value
        const response = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/app-service/value-prop-model?vpId=${id}&preview=true`, {
            method: 'GET',
            credentials: 'include'
        })

        if (response.status !== 200) {
            throw 'Could not get model'
        }

        const json = await response.json()

        window['tree'] = createTree(json, givingTreeStore)
    }

    const getLibrarySlide = async (paramId: string, vmId: string, slideLibraryId: string) => {
        await setUpSlide(paramId, vmId, slideLibraryId)
        slideEditorLoading.value = false
        valueModelLoaded.value = true
    }

    const getLibrarySlideElements = async (slideTitle: string) => {
        const response = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/value-prop-slide/0/${slideTitle}`, {
            method: 'GET'
        })
        const data = await response.json()


        slidesStore.setSlides(data.slides)
    }

    const librarySlides = async (url: string) => {
        const filePathSanitized = url.replace(/([^\/]+)$/, (match) =>
            match.replace(/\s+/g, '-').replace(/[^\w\-]/g, '')
        );
        const response = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/library-slide-data?url=${filePathSanitized}`, {
            method: 'GET'
        })

        const data = await response.json()

        return data.slides

    }

    const emitUserDataForForm = (data: any, userId: string = '') => {
        const copy = JSON.parse(JSON.stringify(data))
        const message = {
            'type': 'lpi-calculator-data',
            ...copy
        }

        if (userId) {
            message.lpiUserId = userId
        }

        window.top.postMessage(message, "*");
    }

    const setCalculatorUserInputs = async (calcId: string, data: any) => {
        const token = localStorage.getItem('lpi-calc-token')  ?? ''
        await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/public-vp/${calcId}/user-inputs?token=${token}`, {
            method: 'PUT',
            headers: {
                'content-type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify(data),
        });
    }

    const setCalculatorUser = async (calcId: string, data: any) => {
        const token = localStorage.getItem('lpi-calc-token')  ?? ''
        await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/public-vp/${calcId}/user?token=${token}`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify(data),
        });
    }

    const getCalculatorUserInputs = async (calcId: string) => {
        const token = localStorage.getItem('lpi-calc-token')  ?? ''
        const response = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/public-vp/${calcId}/user-inputs?token=${token}`, {
            method: 'GET',
        })

        const json = await response.json()
        if (json.token) {
            localStorage.setItem('lpi-calc-token', json.token)
        }
        return json
    }

    const saveSlideToCalcBucket = async (calcId: string, data: any) => {
        await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/public-vp/${calcId}/save-calc-slide`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify(data),
        });
    }

    const getSlideFromCalcBucket = async (calcId: string) => {
        const response = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/public-vp/data/${calcId}/slide.json`, {
            method: 'GET'
        })

        return  await response.json()
    }

    const getUvpPiniaData = async (storeName: string, uvpId: string) => {
        const response = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/uvp-pinia-data/${storeName}/${uvpId}`, {
            method: 'GET'
        })

        return  await response.json()
    }

    const saveUvpPiniaData = async (storeName: string, uvpId: string, data: any) => {
        await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/uvp-pinia-data/${storeName}/${uvpId}`, {
            method: 'PUT',
            headers: {
                'content-type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify(data)
        })
    }

    const fetchAiDataForButton = async ({ category = null, variableName = null, associatedRoots = null, formulaSummary = null, customPrompt = null, chatHistory = null}) => {
        const response = await fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/ai-value-prop-button-prompt`, {
            method: 'POST',
            body: JSON.stringify({category, associatedRoots, variableName, formulaSummary, customPrompt, chatHistory})
        })

        return response.json()

    }

    const importFontsFromWebsite = async (url: string) => {
        const response = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/file-service/customer-fonts`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify({vmId: valueModelId.value, url: url})
        })

        return await response.json()
    }

    const getCustomerFonts = async () => {
        const response = await window.fetch(`${import.meta.env.VITE_APP_ENV_SERVER}/lpi-service/file-service/customer-fonts/${valueModelId.value}`, {
            method: 'GET'
        })

        return  await response.json()
    }

    return {
        setUpSlide,
        savePresentation,
        forceSavePresentation,
        loadValueModel,
        initValueModelEventListener,
        updateSlide,
        publishPresentation,
        getLibrarySlide,
        getLibrarySlideElements,
        librarySlides,
        setCalculatorUserInputs,
        getCalculatorUserInputs,
        setVpSlideData,
        getUvpPiniaData,
        saveUvpPiniaData,
        getModified,
        emitUserDataForForm,
        fetchAiDataForButton,
        importFontsFromWebsite,
        getCustomerFonts,
        setCalculatorUser
    }
}
